// This file is part of re-motion
// Copyright (c) rubicon IT GmbH, www.rubicon.eu
// 
import { NetworkType } from "@lib/messaging/NetworkType";
import { Assert } from "@lib/utilities/Assert";

/**
 * Represents a combination of network type and network identity.
 * The network identity uniquely identifies a client inside of a network type.
 */
export class NetworkSource
{
  private static readonly networkTypeRegex = /^\w+$/;
  private static readonly networkIdentifierRegex = /^(\w|\.)*$/;
  private static readonly networkSourceRegex = /^(\w+)(#(\w|\.)+)?$/;

  static compare(a: NetworkSource, b: NetworkSource): boolean
  {
    return a.networkType === b.networkType && (a.networkIdentifier.length === 0 || b.networkIdentifier.length === 0 || a.networkIdentifier === b.networkIdentifier);
  }

  static compareStrict(a: NetworkSource, b: NetworkSource): boolean
  {
    return a.networkType === b.networkType && a.networkIdentifier === b.networkIdentifier;
  }

  static create(networkType: NetworkType, networkIdentifier?: string): NetworkSource
  {
    if (!this.isValidNetworkType(networkType))
      throw new Error("The specified network type is invalid.");
    if (networkIdentifier !== undefined && !this.isValidNetworkIdentifier(networkIdentifier))
      throw new Error("The specified network identifier is invalid.");

    return new NetworkSource(networkType, networkIdentifier || "");
  }

  static isValidNetworkSource(networkSource: string): boolean
  {
    return NetworkSource.networkSourceRegex.test(networkSource);
  }

  static isValidNetworkType(networkType: string): boolean
  {
    return NetworkSource.networkTypeRegex.test(networkType);
  }

  static isValidNetworkIdentifier(networkIdentifier: string): boolean
  {
    return NetworkSource.networkIdentifierRegex.test(networkIdentifier);
  }

  static parse(networkIdentifier: string): NetworkSource
  {
    const match = this.networkSourceRegex.exec(networkIdentifier);
    if (match == null)
      throw new Error("The specified network identifier is invalid.");

    const networkType = match[1] as NetworkType;
    switch (networkType)
    {
      case "website":
      case "content":
      case "background":
      case "native":
        break;
      default:
        Assert.isNever(networkType);
        throw new Error("The specified network identifier is invalid.");
    }

    // e.g. match for "abc#3.4" -> [0] = "abc#3.4", [1] = "abc", [2] = "#3.4"
    if (match[2])
      return new NetworkSource(networkType, match[2].substr(1));

    return new NetworkSource(networkType, "");
  }

  readonly networkType: NetworkType;
  readonly networkIdentifier: string;

  private constructor(networkType: NetworkType, networkIdentifier: string)
  {
    this.networkType = networkType;
    this.networkIdentifier = networkIdentifier;
  }

  equals(networkType: NetworkType): boolean;
  equals(networkSource: NetworkSource): boolean;
  equals(value: NetworkType | NetworkSource): boolean
  {
    if (typeof value === "string")
      return this.networkType === value;

    return NetworkSource.compareStrict(this, value);
  }

  toString(): string
  {
    return this.networkIdentifier ? `${this.networkType}#${this.networkIdentifier}` : this.networkType;
  }

  withoutNetworkIdentifier(): NetworkSource
  {
    return NetworkSource.create(this.networkType);
  }

  withNetworkIdentifier(networkIdentifier: string): NetworkSource
  {
    return NetworkSource.create(this.networkType, networkIdentifier);
  }
}